import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CollectionSelect from 'components/application/CollectionSelect';
import ErrorMessage from 'components/application/ErrorMessage';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { resourcesAsOptions } from 'components/helpers/forms';

export default function RolesForm({
  assignableCourses,
  currentRole,
  onCourseCompanyRoleDelete,
  onCourseCompanyRoleRequiredChange,
  onInputChange,
  onOptionChange,
  removeErrorStyling,
  requestError,
  sidePanelFieldRef,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'roles.RolesForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  // filter out already selected courses
  const selectedCourseIds =
    currentRole.courseCompanyRoles ?
      currentRole.courseCompanyRoles.map((ccr) => ccr.courseId)
    : [];

  const selectableCourses = assignableCourses.filter(
    (course) => !selectedCourseIds.includes(course.id),
  );

  // order courseCompanyRoles alphabetically
  const orderedCourseCompanyRoles = currentRole.courseCompanyRoles
    .map((courseCompanyRole) => {
      const { courseId } = courseCompanyRole;
      const courseName = assignableCourses.find(
        (course) => course.id === courseId,
      ).attributes.name;
      return {
        courseId,
        courseName,
        required: courseCompanyRole.required,
      };
    })
    .sort((a, b) =>
      a.courseName.localeCompare(b.courseName, 'en', { sensitivity: 'base' }),
    );

  return (
    <>
      <div className='popup__body-form'>
        <ErrorMessage
          isFallback={requestError.isFallback}
          validationErrors={requestError.validationErrors}
          wrapperClassName='form-container'
        />
        <div className='form-container -tw-mb-6'>
          <TextField
            fieldError={requestError.validationErrors.position}
            inputRef={sidePanelFieldRef}
            isRequired={true}
            label={i18nTCommon('shared.role')}
            name='position'
            onChange={onInputChange}
            removeErrorStyling={removeErrorStyling}
            value={currentRole.position}
          />
        </div>
      </div>
      {currentActor.isAllowedFeature('training_register') && (
        <>
          <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />
          <div className='popup__body-form tw-mb-8 tw-pb-0'>
            <div className='form-container'>
              <CollectionSelect
                externalProps={{
                  additionalNoOptionsMessage: i18nTCommon(
                    'courses.newCoursesMustBeAddedFromCourseRegister',
                  ),
                }}
                isSearchable={true}
                label={i18nT('courseIds.label')}
                modifiers={['expandable']}
                name='courseIds'
                onChange={(e) => onOptionChange(e.value.toString())}
                options={resourcesAsOptions({
                  resourcesCollection: selectableCourses,
                  resourceIdentifier: 'name',
                })}
                placeholder={i18nTCommon('courses.search')}
                value={null}
              />
            </div>
          </div>
        </>
      )}

      {currentActor.isAllowedFeature('training_register') && (
        <div className='popup__body-form tw-mb-8 tw-py-0'>
          <div className='form-container'>
            {orderedCourseCompanyRoles.map((courseCompanyRole) => (
              <div
                className='tw-group/pill chip-item-pill tw-relative tw-mb-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-bg-grey-050 tw-px-3 tw-py-2.5'
                key={courseCompanyRole.courseId}
              >
                <div className='tw-text-s tw-font-medium tw-tracking-wide'>
                  {courseCompanyRole.courseName}
                </div>

                <div className='tw-flex tw-items-center'>
                  <div className='tw-w-28'>
                    <CollectionSelect
                      label=''
                      modifiers={['pill']}
                      name='requiredCCR'
                      onChange={(e) =>
                        onCourseCompanyRoleRequiredChange(
                          courseCompanyRole.courseId,
                          e.value,
                        )
                      }
                      options={[
                        { value: true, label: i18nTCommon('shared.required') },
                        { value: false, label: i18nTCommon('shared.optional') },
                      ]}
                      value={
                        courseCompanyRole.required ?
                          { value: true, label: i18nTCommon('shared.required') }
                        : {
                            value: false,
                            label: i18nTCommon('shared.optional'),
                          }
                      }
                    />
                  </div>
                  <span
                    className="collection-select__multivalue-remove circle--remove tw-ml-2 tw-bg-transparent before:tw-bg-grey-700 before:tw-content-[''] after:tw-bg-grey-700 after:tw-content-[''] hover:tw-bg-red-600 hover:before:tw-bg-white hover:after:tw-bg-white"
                    onClick={() => {
                      onCourseCompanyRoleDelete(courseCompanyRole.courseId);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

RolesForm.propTypes = {
  currentRole: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onOptionChange: PropTypes.func.isRequired,
};
